















































import { Component, Prop, Watch, Vue } from "vue-property-decorator";
import ConfigFileService from "@/services/config_file_service";
import { APIError, NotFoundError } from "@/services/error_service";

@Component
export default class ConfigCodeModal extends Vue {
  @Prop({ default: false }) showDialog!: boolean;
  
  protected configFileService = new ConfigFileService();
  protected show = false;
  protected codeDigits: string[] = ['', '', '', '', ''];
  protected errorMessage = '';
  protected hasError = false;

  @Watch("showDialog", { immediate: true })
  protected isOpen(): void {
    this.show = this.showDialog;
    if (this.show) {
      this.resetForm();
      this.$nextTick(() => {
        this.focusFirstInput();
      });
    }
  }

  protected closeDialog(): void {
    this.show = false;
    this.$emit('close');
  }

  protected resetForm(): void {
    this.codeDigits = ['', '', '', '', ''];
    this.errorMessage = '';
    this.hasError = false;
  }

  protected focusFirstInput(): void {
    const firstInput = this.$refs.codeInput0 as HTMLInputElement[];
    if (firstInput && firstInput[0]) {
      firstInput[0].focus();
    }
  }

  protected handleInput(index: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value.replace(/[^0-9A-Za-z]/g, '').toUpperCase(); // Allow alphanumeric, convert to uppercase
    
    // If multiple characters were entered (like from paste), handle it specially
    if (value.length > 1) {
      this.handleMultipleInput(index, value);
      return;
    }
    
    // Single character input - normal behavior
    this.codeDigits[index] = value;
    this.clearError();

    // Auto-focus next input if current is filled
    if (value && index < 4) {
      const nextInput = this.$refs[`codeInput${index + 1}`] as HTMLInputElement[];
      if (nextInput && nextInput[0]) {
        nextInput[0].focus();
      }
    }
  }

  protected handleMultipleInput(startIndex: number, value: string): void {
    // Clean the value to allow alphanumeric characters and convert to uppercase
    const cleanValue = value.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
    
    // Clear all fields first
    this.codeDigits = ['', '', '', '', ''];
    
    // Fill in the characters starting from the beginning
    const charsToFill = Math.min(cleanValue.length, 5);
    for (let i = 0; i < charsToFill; i++) {
      this.codeDigits[i] = cleanValue[i];
    }
    
    this.clearError();
    
    // Focus the appropriate field after filling
    this.$nextTick(() => {
      if (charsToFill < 5) {
        // If not all fields are filled, focus the next empty field
        const nextEmptyInput = this.$refs[`codeInput${charsToFill}`] as HTMLInputElement[];
        if (nextEmptyInput && nextEmptyInput[0]) {
          nextEmptyInput[0].focus();
        }
      } else {
        // If all fields are filled, focus the last field
        const lastInput = this.$refs.codeInput4 as HTMLInputElement[];
        if (lastInput && lastInput[0]) {
          lastInput[0].focus();
        }
      }
    });
  }

  protected handleKeydown(index: number, event: KeyboardEvent): void {
    // Handle backspace to move to previous input
    if (event.key === 'Backspace' && !this.codeDigits[index] && index > 0) {
      const prevInput = this.$refs[`codeInput${index - 1}`] as HTMLInputElement[];
      if (prevInput && prevInput[0]) {
        prevInput[0].focus();
      }
    }
    
    // Handle Enter key to submit
    if (event.key === 'Enter' && this.isCodeComplete) {
      this.submitCode();
    }
  }

  protected handlePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text').replace(/[^0-9A-Za-z]/g, '').toUpperCase() || '';
    
    if (pastedData.length > 0) {
      // Clear all fields first
      this.codeDigits = ['', '', '', '', ''];
      
      // Fill in the pasted characters (up to 5)
      const charsToFill = Math.min(pastedData.length, 5);
      for (let i = 0; i < charsToFill; i++) {
        this.codeDigits[i] = pastedData[i];
      }
      
      this.clearError();
      
      // Focus the appropriate field after pasting
      this.$nextTick(() => {
        if (charsToFill < 5) {
          // If not all fields are filled, focus the next empty field
          const nextEmptyInput = this.$refs[`codeInput${charsToFill}`] as HTMLInputElement[];
          if (nextEmptyInput && nextEmptyInput[0]) {
            nextEmptyInput[0].focus();
          }
        } else {
          // If all fields are filled, focus the last field
          const lastInput = this.$refs.codeInput4 as HTMLInputElement[];
          if (lastInput && lastInput[0]) {
            lastInput[0].focus();
          }
        }
      });
    }
  }

  protected clearError(): void {
    this.errorMessage = '';
    this.hasError = false;
  }

  protected showError(message: string): void {
    this.errorMessage = message;
    this.hasError = true;
  }

  get isCodeComplete(): boolean {
    return this.codeDigits.every(digit => digit !== '');
  }

  get configCode(): string {
    return this.codeDigits.join('');
  }

  protected async submitCode(): Promise<void> {
    if (!this.isCodeComplete) {
      this.showError('Please enter a complete 5-digit code.');
      return;
    }

    try {
      this.clearError();
      // Redirect to the shared configuration view
      this.$router.push(`/view?design=${this.configCode}`);
      this.closeDialog();
    } catch (err) {
      if (err instanceof NotFoundError) {
        this.showError('Configuration not found. Please check your code.');
      } else {
        this.showError('An error occurred. Please try again.');
      }
    }
  }
}
